export interface IncomePaymentWhTaxItem {
  subTotal?: number | string | null
}

/**
 * Calculate withholding tax for an income payment.
 *
 * Most income documents provide line items, so their existing per-item
 * calculation remains unchanged. Billing notes do not have their own income
 * items; in that case the outstanding payment amount is the tax base.
 * The value 99 means "custom" and must still be entered manually.
 */
export const incomePaymentWhTaxPrice = (
  totalAmount: number,
  items: IncomePaymentWhTaxItem[] = [],
  whTaxPercent: number = 0
) => {
  if (!items.length) {
    return whTaxPercent === 99 ? 0 : totalAmount * (whTaxPercent / 100)
  }

  const whTax = whTaxPercent / 100

  return items.reduce((sum, item) => {
    if (whTaxPercent !== 99) {
      return sum + (item?.subTotal ? Number(item.subTotal) * whTax : totalAmount * whTax)
    }

    return sum + totalAmount * whTax
  }, 0)
}

interface WhTaxBaseItem {
  beforeVatPrice?: number | string | null
  vatPrice?: number | string | null
}

const roundSatang = (value: number) => Math.round(Number((value * 100).toFixed(6))) / 100

/**
 * ภาษีหัก ณ ที่จ่ายคิดจากยอดก่อน VAT ใบที่มีรายการจึงลดฐานตามสัดส่วน VAT ของใบ
 * บิลยกมาไม่มีรายการ แยก VAT ไม่ได้ ฐานจึงเป็นยอดเต็ม
 */
export const whTaxBaseRatio = (items: WhTaxBaseItem[] = []) => {
  const beforeVat = items.reduce((sum, item) => sum + Number(item?.beforeVatPrice || 0), 0)
  const vat = items.reduce((sum, item) => sum + Number(item?.vatPrice || 0), 0)

  return beforeVat > 0 && vat > 0 ? beforeVat / (beforeVat + vat) : 1
}

interface ApplyWhTaxRateInput {
  rowAmount: number | string
  otherRowsAmount?: number
  whTaxPrice?: number | string
  whTaxPercent: number
  baseRatio?: number
}

/**
 * เลือกอัตราในหน้าต่างรับ/จ่ายชำระ: ยอดในช่อง + ภาษีเดิม คือยอดที่ตัดหนี้ ยอดที่พิมพ์ไว้จึงไม่หาย
 * ภาษีปัดเป็นสตางค์ แล้วแถวแรกรับเงินจริงเท่ายอดตัดหนี้ลบภาษี สองยอดรวมกันจึงพอดีเสมอ
 */
export const applyWhTaxRateToPayment = ({
  rowAmount,
  otherRowsAmount = 0,
  whTaxPrice = 0,
  whTaxPercent,
  baseRatio = 1,
}: ApplyWhTaxRateInput) => {
  const rowSettled = Number(rowAmount || 0) + Number(whTaxPrice || 0)
  const rate = whTaxPercent === 99 ? 0 : (whTaxPercent / 100) * baseRatio
  const nextWhTaxPrice = roundSatang((rowSettled + otherRowsAmount) * rate)

  return {amount: roundSatang(rowSettled - nextWhTaxPrice), whTaxPrice: nextWhTaxPrice}
}

/**
 * พิมพ์มูลค่าภาษีเอง (บิลยกมาไม่มีข้อมูลแยก VAT เช่น 5,350 รวม VAT หัก 3% ของ 5,000 = 150)
 * ยอดตัดหนี้ของแถวคงเดิม เงินจ่ายจริงจึงเปลี่ยนตาม ภาษีเกินยอดตัดหนี้ไม่ได้ คืน null
 */
export const applyWhTaxAmountToPayment = ({
  rowAmount,
  whTaxPrice = 0,
  nextWhTaxPrice,
}: {
  rowAmount: number | string
  whTaxPrice?: number | string
  nextWhTaxPrice: number
}) => {
  const rowSettled = Number(rowAmount || 0) + Number(whTaxPrice || 0)
  if (nextWhTaxPrice < 0 || nextWhTaxPrice > rowSettled + 0.005) return null

  return {amount: roundSatang(rowSettled - nextWhTaxPrice), whTaxPrice: nextWhTaxPrice}
}

/**
 * เลือกอัตราแล้วมาแก้ยอดทีหลัง: ยอดที่พิมพ์คือเงินที่รับ/จ่ายจริง (เช่นลูกค้าโอนมา 990 หัก 1%)
 * ช่องยอดจึงไม่เปลี่ยนใต้มือคนพิมพ์ ภาษีคิดย้อนจากยอดตัดหนี้ = 990 + 10
 */
export const whTaxFromPaidAmount = (
  paidAmount: number,
  whTaxPercent: number,
  baseRatio: number = 1
) => {
  const rate = whTaxPercent === 99 ? 0 : (whTaxPercent / 100) * baseRatio
  if (rate <= 0 || rate >= 1) return 0

  return roundSatang((paidAmount * rate) / (1 - rate))
}

/** แก้ยอดแถวไหนก็ตาม ภาษีอยู่ที่แถวแรก แถว "ไม่มี" กับ "กำหนดเอง" ไม่แตะภาษีที่มีอยู่ */
export const recalculateWhTaxFromPayments = (payments: any[], baseRatio: number = 1) => {
  const whTaxPercent = Number(payments[0]?.whTax || 0)
  if (whTaxPercent <= 0 || whTaxPercent === 99 || payments[0]?.whTaxManual) return payments

  const paidAmount = payments.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0)

  return payments.map((payment, index) =>
    index === 0
      ? {...payment, whTaxPrice: whTaxFromPaidAmount(paidAmount, whTaxPercent, baseRatio)}
      : payment
  )
}
